import React, { useState, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useApiQuery } from "../../hooks/useApi";
import { useReactToPrint } from "react-to-print";

// Printable Receipt Component
const PrintableReceipt = React.forwardRef(({ receipt, client }, ref) => (
  <div
    ref={ref}
    style={{
      padding: "40px",
      width: "420px",
      margin: "0 auto",
      fontFamily: "monospace",
    }}
  >
    <style>{`
      @media print {
        @page {
          size: A5;
          margin: 10mm;
        }
      }
    `}</style>

    <h2 style={{textAlign: "center"}}>Fiscale</h2>
    <p style={{fontSize:"12px",textAlign: "center", marginBottom: "20px" }}>Koach Gaon, Biswanath Chariali, Assam, 784176, +91 6901945275</p>

    <p><strong>Receipt No:</strong> {receipt?.receiptNumber}</p>
    <p><strong>Date:</strong> {new Date(receipt?.collectedDate || Date.now()).toLocaleDateString()}</p>
    <p><strong>Amount:</strong> ₹{receipt?.amount}</p>
    <p><strong>Client Name:</strong> {client?.name}</p>
    <p><strong>Phone Number:</strong> {client?.phoneNumber}</p>
    <p><strong>Mode of Payment:</strong> {receipt?.paymentMethod}</p>
    {/* {receipt?.bank && <p><strong>Bank:</strong> {receipt.bank}</p>} */}
    {receipt?.transactionReference && (
      <p><strong>Transaction Ref:</strong> {receipt.transactionReference}</p>
    )}
    <p><strong>Collected By:</strong> {receipt?.collectedBy?.name || "—"}</p>
    <p style={{fontSize:"10px"}}>Received with thanks an amount of Rs. {receipt?.amount}/- (One Thousand Five Hundred only) from {client?.name}. Cheques are subject to realisation.</p>
    <hr style={{ margin: "20px 0" }} />

    <p style={{ textAlign: "center", opacity: 0.7 }}>Thank you for choosing Fiscale</p>
  </div>
));

function AllReceiptsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const receiptRef = useRef();

  const { data: receipts, isLoading, error, refetch } = useApiQuery(
    ["receipts"],
    "/receipts"
  );

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt_${selectedReceipt?.receiptNumber || "Unknown"}`,
    onAfterPrint: () => {
      setPrintDialogOpen(false);
      setSelectedReceipt(null);
    },
  });

  const openPrintDialog = (receipt) => {
    setSelectedReceipt(receipt);
    setPrintDialogOpen(true);
  };

  const filteredReceipts = useMemo(() => {
    if (!receipts || !Array.isArray(receipts)) return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return receipts;

    return receipts.filter((receipt) => {
      const receiptNumber = receipt.receiptNumber?.toLowerCase() || "";
      const receiptId = receipt.receiptId?.toLowerCase() || "";
      const clientName = receipt.clientId?.name?.toLowerCase() || "";
      const phoneNumber = receipt.clientId?.phoneNumber?.toLowerCase() || "";
      const amount = receipt.amount?.toString() || "";
      const paymentMethod = receipt.paymentMethod?.toLowerCase() || "";
      const bank = receipt.bank?.toLowerCase() || "";

      return (
        receiptNumber.includes(query) ||
        receiptId.includes(query) ||
        clientName.includes(query) ||
        phoneNumber.includes(query) ||
        amount.includes(query) ||
        paymentMethod.includes(query) ||
        bank.includes(query)
      );
    });
  }, [receipts, searchQuery]);

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      cash: "primary",
      bank_transfer: "success",
      cheque: "info",
      upi: "secondary",
      card: "warning",
    };
    return methodColors[method?.toLowerCase()] || "default";
  };

  const getClientName = (receipt) => {
    if (typeof receipt.clientId === "object" && receipt.clientId?.name) {
      return receipt.clientId.name;
    }
    return receipt.clientName || "Unknown Client";
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading receipts: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Fee Receipts
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Search and manage all fee receipts
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search by Receipt Number, Receipt ID, Client Name, Phone, Amount, Payment Method, or Bank..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="textSecondary">
            {filteredReceipts.length} receipt{filteredReceipts.length !== 1 ? "s" : ""} found
            {searchQuery && ` for "${searchQuery}"`}
          </Typography>
          {searchQuery && (
            <Button size="small" onClick={handleClearSearch}>
              Clear Search
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {filteredReceipts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {searchQuery ? "No receipts found" : "No receipts available"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {searchQuery
                ? "Try adjusting your search criteria"
                : "Receipts will appear here once they are created"}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Receipt No.</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.map((receipt) => (
                  <TableRow key={receipt._id || receipt.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {receipt.receiptNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {getClientName(receipt)}
                      </Typography>
                      {receipt.clientId?.phoneNumber && (
                        <Typography variant="caption" color="textSecondary">
                          {receipt.clientId.phoneNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ₹{(receipt.amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={receipt.paymentMethod?.toUpperCase()}
                        color={getPaymentMethodColor(receipt.paymentMethod)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {receipt.bank ? (
                        <Chip
                          label={receipt.bank}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(receipt.collectedDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(receipt.collectedDate).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Print Receipt">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => openPrintDialog(receipt)}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Print Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => {
          setPrintDialogOpen(false);
          setSelectedReceipt(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Receipt Preview</Typography>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>
          {selectedReceipt && (
            <PrintableReceipt
              ref={receiptRef}
              receipt={selectedReceipt}
              client={selectedReceipt.clientId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AllReceiptsPage;